/**
 * Structured data (JSON-LD) for public event detail pages.
 * Emits schema.org Event plus BreadcrumbList aligned with `PageBreadcrumb` on the route.
 */

import { env } from '@web/src/lib/env.client'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import {
  geoPhraseForLocationDescription,
  plainTextFromRichContent,
  SEO_META_DESCRIPTION_MAX_CHARS,
  sameAsFromWebLinks,
  truncateMetaDescription,
} from '@web/src/lib/seo/seo-common'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'
import type { EventCategoryEnum, EventQuery } from 'types/graphql'

type EventForJsonLd = NonNullable<EventQuery['event']>

/** Human-readable category for prose and schema (English; common practice for structured data). */
function schemaEventCategoryLabel(category: EventCategoryEnum): string {
  switch (category) {
    case 'KINK':
      return 'Kink lifestyle event'
    case 'CAMPING':
      return 'Camping event'
    case 'CLUBS':
      return 'Club event'
    case 'HOUSE_PARTIES':
      return 'House party'
    case 'SOCIAL_AND_MEET':
      return 'Social meetup'
    case 'TAKEOVER':
      return 'Takeover event'
    case 'CRUISES':
      return 'Cruise event'
    case 'VACATION':
      return 'Vacation event'
    case 'OTHER':
      return 'Lifestyle event'
    default: {
      const _exhaustive: never = category
      return _exhaustive
    }
  }
}

function categoryPhraseForSentence(category: EventCategoryEnum): string {
  const label = schemaEventCategoryLabel(category)
  return label.charAt(0).toLowerCase() + label.slice(1)
}

/**
 * Short category phrase for the HTML `<title>` (English; common SEO practice).
 */
function categorySegmentForSeoTitle(category: EventCategoryEnum): string {
  switch (category) {
    case 'KINK':
      return 'Kink Event'
    case 'CAMPING':
      return 'Camping Event'
    case 'CLUBS':
      return 'Club Event'
    case 'HOUSE_PARTIES':
      return 'House Party'
    case 'SOCIAL_AND_MEET':
      return 'Social Meetup'
    case 'TAKEOVER':
      return 'Takeover Event'
    case 'CRUISES':
      return 'Cruise Event'
    case 'VACATION':
      return 'Vacation Event'
    case 'OTHER':
      return 'Lifestyle Event'
    default: {
      const _exhaustive: never = category
      return _exhaustive
    }
  }
}

function formatEventStartForMeta(iso: string | null | undefined): string {
  if (!iso?.trim()) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Meta description and JSON-LD `description`: unique per event; name + category + geography + start date,
 * then tagline or plain-text excerpt from the long description. Length-limited for SERP display.
 */
export function buildEventDescription(event: EventForJsonLd): string {
  const name = event.name?.trim() || 'Event'
  const kind = categoryPhraseForSentence(event.category)
  const geo = event.location?.trim()
    ? geoPhraseForLocationDescription(event.location, Boolean(event.locationExact))
    : ''
  const dateStr = formatEventStartForMeta(event.startDate)
  const tagLine = event.tagLine?.trim()
  const bodyPlain = plainTextFromRichContent(event.description ?? undefined)

  let base: string
  if (geo && dateStr) {
    base = `${name} is a ${kind} ${geo} on ${dateStr}.`
  } else if (dateStr) {
    base = `${name} is a ${kind} on ${dateStr}.`
  } else if (geo) {
    base = `${name} is a ${kind} ${geo}.`
  } else {
    base = `${name} is a ${kind}.`
  }

  const detail = tagLine || bodyPlain
  const out = detail ? `${base} ${detail}` : base

  if (out.length <= SEO_META_DESCRIPTION_MAX_CHARS) return out

  if (detail) {
    const separatorLen = 1
    const budget = SEO_META_DESCRIPTION_MAX_CHARS - base.length - separatorLen
    if (budget >= 40) {
      const truncatedDetail = truncateMetaDescription(detail, budget)
      return `${base} ${truncatedDetail}`
    }
  }

  return truncateMetaDescription(out, SEO_META_DESCRIPTION_MAX_CHARS)
}

/**
 * BreadcrumbList aligned with `PageBreadcrumb` on the event detail route:
 * Home → Event Categories → Discover Events → current event.
 */
function buildEventBreadcrumbListJsonLd(eventName: string, eventPageUrl: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Event Categories', item: absoluteUrlForPath('/event') },
      { '@type': 'ListItem', position: 3, name: 'Discover Events', item: absoluteUrlForPath('/event/list') },
      { '@type': 'ListItem', position: 4, name: eventName, item: eventPageUrl },
    ],
  }
}

/** schema.org Person for the event organizer (owner profile). */
function buildEventOrganizerJsonLd(owner: NonNullable<EventForJsonLd['owner']>): Record<string, unknown> {
  const profileUrl = absoluteUrlForPath('/auth/signup')
  const imageUrl = owner.profileMedia?.[0]?.imageUrlMedium
  const name = owner.name?.trim() || 'Event organizer'

  return {
    '@type': 'Person',
    name,
    url: profileUrl,
    ...(imageUrl ? { image: imageUrl } : {}),
  }
}

function schemaOrgEventStatusUrl(status: EventForJsonLd['status']): string {
  if (status === 'CANCELLED') return 'https://schema.org/EventCancelled'
  return 'https://schema.org/EventScheduled'
}

export function buildEventPageJsonLd(input: { event: EventForJsonLd; description: string }): Record<string, unknown>[] {
  const { event, description: eventDescription } = input

  const pagePath = `/event/${event.slug ?? event.id}`
  const url = `${baseUrl()}${pagePath}`
  const entityId = `${url}#event`

  const trimmedDescription = eventDescription.trim()
  const organizerFromOwner = event.owner ? buildEventOrganizerJsonLd(event.owner) : undefined

  const locationName = event.location?.trim() || 'Location TBD'

  const mainEntity: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@id': entityId,
    '@type': 'Event',
    name: event.name,
    ...(trimmedDescription ? { description: trimmedDescription } : {}),
    url,
    startDate: event.startDate,
    ...(event.endDate ? { endDate: event.endDate } : {}),
    eventStatus: schemaOrgEventStatusUrl(event.status),
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    ...(organizerFromOwner && { organizer: organizerFromOwner }),
    location: {
      '@type': 'Place',
      name: locationName,
      ...(event.location && {
        address: {
          '@type': 'PostalAddress',
          addressLocality: event.location,
        },
      }),
      ...(event.locationExact &&
        event.locationLat != null &&
        event.locationLong != null && {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: event.locationLat,
            longitude: event.locationLong,
          },
        }),
    },
    ...(event.attachedMedia &&
      event.attachedMedia.length > 0 && {
        image: event.attachedMedia.slice(0, 5).map((media) => media.imageUrlMedium),
      }),
    ...(event.tags &&
      event.tags.length > 0 && {
        keywords: event.tags.map((tag) => tag.name).join(', '),
      }),
  }

  const sameAs = sameAsFromWebLinks(event.webLinks)
  if (sameAs?.length) {
    mainEntity.sameAs = sameAs.length === 1 ? sameAs[0] : sameAs
  }

  const breadcrumbList = buildEventBreadcrumbListJsonLd(event.name.trim() || 'Event', url)

  return [mainEntity, breadcrumbList]
}

/** Loader fields used for `<head>` SEO on the event detail route. */
export type EventPageSeoLoaderInput = {
  event?: EventQuery['event'] | null
}

export function eventPageHeadFromLoaderData(loaderData: EventPageSeoLoaderInput | null | undefined) {
  const event = loaderData?.event ?? undefined

  if (!event) {
    return seo({ title: `Event | ${env.VITE_APP_NAME}` })
  }

  const eventDescription = buildEventDescription(event)
  const jsonLdData = buildEventPageJsonLd({
    event,
    description: eventDescription,
  })

  const eventTitleName = event.name?.trim() || 'Event'
  const categoryTitle = categorySegmentForSeoTitle(event.category)

  return seo({
    title: `${eventTitleName} | ${categoryTitle} | ${env.VITE_APP_NAME}`,
    description: eventDescription,
    author: event.owner?.name,
    url: `${baseUrl()}/event/${event.slug ?? event.id}`,
    image: event.attachedMedia?.[0]?.imageUrlMedium,
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd: jsonLdData,
  })
}
